// Local-storage draft for the gift flow. We persist a slim snapshot of the
// user's progress so they can close the tab / browser and resume later.
//
// First save fires after `saveGiftUser` succeeds (i.e. once we have a
// `giftUserUid` from the backend). Before that, nothing is saved.
//
// Wiped on:
//   • payment success (gift is complete — no more draft)
//   • explicit "Start Over" from the resume popup
//   • TTL expiry (currently 2 days)
//   • read failure / version mismatch
//
// We deliberately DO NOT persist:
//   • File objects from selectedFiles (can't survive a reload)
//   • In-progress uploads (PENDING / UPLOADING / PAUSED — blob URLs gone)
//   • Stripe clientSecret (short-lived; regenerate on resume)
//   • UI / popup / modal state

const STORAGE_KEY = 'giftDraft:v1';
const SCHEMA_VERSION = 1;
const TTL_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

// Upload statuses we keep when serializing (everything else is dropped).
const TERMINAL_KEEP_STATUSES = new Set(['completed', 's3_complete']);

// Keep only URL flavours that survive a page reload. blob: URLs reference
// the previous page's memory and are dead the moment we navigate / reload,
// so persisting them is worse than dropping them (it makes <img> show a
// broken icon). data: URLs and http(s): URLs are fine to keep verbatim.
const isPersistableUrl = (u) => typeof u === 'string'
  && u.length > 0
  && !u.startsWith('blob:');

const safeWrite = (raw) => {
  try { localStorage.setItem(STORAGE_KEY, raw); } catch (e) { /* quota / private mode */ }
};

const safeRead = () => {
  try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
};

// Trim a `selectedFiles` entry down to what's safe to persist. The `file`
// (File object) and any blob: URLs (previewUrl, posterUrl) won't survive a
// reload, so they're dropped. We keep names, sizes, server identifiers,
// and any data: / http(s): thumbnail URLs so step 3 still renders the
// existing files with thumbnails when the user resumes.
const slimSelectedFile = (f) => {
  if (!f || !f.id) return null;
  const out = {
    id: f.id,
    name: f.name,
    size: f.size,
    type: f.type,
    source: f.source || 'local',
  };
  if (isPersistableUrl(f.posterUrl)) out.posterUrl = f.posterUrl;
  if (isPersistableUrl(f.previewUrl)) out.previewUrl = f.previewUrl; // server URLs only after S3
  // Server-side identifiers — useful on resume to map to backend records.
  if (f.videoId) out.videoId = f.videoId;
  if (f.albumId) out.albumId = f.albumId;
  if (f.s3Key) out.s3Key = f.s3Key;
  if (f.s3Url) out.s3Url = f.s3Url;
  if (f.fileKey) out.fileKey = f.fileKey;
  if (f.thumbnail) out.thumbnail = f.thumbnail;
  if (typeof f.photoCount === 'number') out.photoCount = f.photoCount;
  if (Array.isArray(f.photos)) {
    out.photos = f.photos.map((p) => {
      if (!p) return null;
      const pp = {
        id: p.id,
        name: p.name,
        size: p.size,
      };
      if (isPersistableUrl(p.previewUrl)) pp.previewUrl = p.previewUrl;
      if (p.s3Key) pp.s3Key = p.s3Key;
      if (p.s3Url) pp.s3Url = p.s3Url;
      return pp;
    }).filter(Boolean);
  }
  return out;
};

// Trim an upload entry down to what's safe to persist. File objects don't
// survive page reload, so we strip them. Backend IDs (videoId / albumId /
// uploadId) are kept so the resumed UI can reattach to existing records.
const slimUpload = (u) => {
  if (!u || !TERMINAL_KEEP_STATUSES.has(u.status)) return null;
  return {
    id: u.id,
    secureUploadId: u.secureUploadId || null,
    fileName: u.fileName,
    fileSize: u.fileSize,
    type: u.type,
    status: u.status,
    progress: 100,
    bytesUploaded: u.fileSize,
    speed: 0,
    timeRemaining: 0,
    // Backend identifiers worth keeping if present.
    videoId: u.videoId || null,
    albumId: u.albumId || null,
    fileKey: u.fileKey || null,
    s3Key: u.s3Key || null,
    backendUploadId: u.backendUploadId || null,
    // Photo album cover previews — these are blob: URLs that won't survive,
    // but the backend ID can fetch a thumbnail when the user resumes.
    photosPreviews: undefined,
  };
};

/**
 * Build the snapshot payload from the in-memory state. Pure — used by both
 * the local-storage writer (saveDraft) and the server-side writer (so both
 * sides agree on the exact shape and we can swap freely between them).
 */
export const buildSnapshotPayload = (snapshot) => {
  if (!snapshot || !snapshot.giftUserUid) return null;
  const slimUploads = (snapshot.uploads || [])
    .map(slimUpload)
    .filter(Boolean);
  // Anchor the persisted selectedFiles list to the upload IDs we kept (only
  // entries the user successfully uploaded). This avoids resurrecting tiles
  // for in-progress / failed work whose blob: URLs are now dead.
  const keepIds = new Set(slimUploads.map((u) => u.id));
  const slimSelectedFiles = (snapshot.selectedFiles || [])
    .filter((f) => f && keepIds.has(f.id))
    .map(slimSelectedFile)
    .filter(Boolean);
  return {
    version: SCHEMA_VERSION,
    savedAt: Date.now(),
    giftUserUid: snapshot.giftUserUid,
    formData: snapshot.formData || null,
    senderName: snapshot.senderName || '',
    senderEmail: snapshot.senderEmail || '',
    currentStep: snapshot.currentStep ?? 0,
    step1SubStep: snapshot.step1SubStep ?? 1,
    selectedChoice: snapshot.selectedChoice || null,
    selectedPlan: snapshot.selectedPlan || null,
    deliveryDate: snapshot.deliveryDate || '',
    // Tokens — duplicated from individual localStorage keys so a single
    // draft snapshot is self-contained and resume is atomic.
    token: snapshot.token || '',
    uploaderToken: snapshot.uploaderToken || '',
    uploaderTokenExpiry: snapshot.uploaderTokenExpiry || '',
    giftUserId: snapshot.giftUserId || '',
    existingUser: !!snapshot.existingUser,
    setpasswordUrl: snapshot.setpasswordUrl || '',
    completedUploads: slimUploads,
    completedSelectedFiles: slimSelectedFiles,
  };
};

/**
 * Save / overwrite the local-storage draft. Called whenever a state change
 * happens that the user would want to recover (formData edits, step
 * transitions, upload completes, etc.). Cheap — runs through JSON.stringify
 * once. Returns the built payload so callers can reuse it for the server
 * write without rebuilding.
 */
export const saveDraft = (snapshot) => {
  const payload = buildSnapshotPayload(snapshot);
  if (!payload) return null; // pre-saveGiftUser → nothing to save
  try {
    safeWrite(JSON.stringify(payload));
  } catch (e) { /* JSON serialization failed — drop silently */ }
  return payload;
};

/**
 * Normalize a `resume_data` value returned by getGiftStatus. The backend
 * stores it as a JSON column, but depending on PHP serialization it may
 * come back as either a parsed object or a JSON string. Handle both, and
 * reject anything that's empty / malformed / wrong-version / completed.
 *
 * Returns { snapshot, completed } — `snapshot` is the restorable payload
 * (or null), `completed` indicates the gift is already done.
 */
export const parseServerResumeData = (raw) => {
  if (raw == null || raw === '') return { snapshot: null, completed: false };
  let parsed = raw;
  if (typeof raw === 'string') {
    try { parsed = JSON.parse(raw); } catch (e) { return { snapshot: null, completed: false }; }
  }
  if (!parsed || typeof parsed !== 'object') return { snapshot: null, completed: false };
  // gift_completed is the current terminal flag; `completed` is the legacy
  // name written by older clients — accept either so old DB rows still work.
  if (parsed.gift_completed === true || parsed.completed === true) return { snapshot: null, completed: true };
  // Tolerate snapshots without `version` (server may have been written by
  // a different client version) — accept if it at least has a giftUserUid.
  if (!parsed.giftUserUid) return { snapshot: null, completed: false };
  return { snapshot: parsed, completed: false };
};

export const clearDraft = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
};

/**
 * Load the draft if one exists, isn't expired, and has the expected shape.
 * Returns null otherwise (and wipes the storage entry on bad data / TTL).
 */
export const loadDraft = () => {
  const raw = safeRead();
  if (!raw) return null;
  let parsed;
  try { parsed = JSON.parse(raw); } catch (e) { clearDraft(); return null; }
  if (!parsed || typeof parsed !== 'object') { clearDraft(); return null; }
  if (parsed.version !== SCHEMA_VERSION) { clearDraft(); return null; }
  if (typeof parsed.savedAt !== 'number') { clearDraft(); return null; }
  if (Date.now() - parsed.savedAt > TTL_MS) { clearDraft(); return null; }
  if (!parsed.giftUserUid) { clearDraft(); return null; }
  // Gift already finished — drop the entry so the "Continue your gift?"
  // popup never offers a completed gift. Accepts the legacy `completed`
  // key too for safety with snapshots written by older clients.
  if (parsed.gift_completed === true || parsed.completed === true) { clearDraft(); return null; }
  return parsed;
};

export const hasDraft = () => loadDraft() !== null;

/**
 * Convenience for the resume popup — returns just the human-readable bits
 * (sender + recipient names/emails) without the rest of the draft.
 * Used to render "From X to Y" in the dialog.
 */
export const peekDraftIdentity = () => {
  const d = loadDraft();
  if (!d) return null;
  const fd = d.formData || {};
  return {
    senderName: d.senderName || '',
    senderEmail: d.senderEmail || '',
    recipientName: [fd.firstname, fd.lastname].filter(Boolean).join(' ').trim(),
    recipientEmail: fd.email || '',
    savedAt: d.savedAt,
  };
};
